import EditIcon from '@mui/icons-material/Edit';
import { Avatar, Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import type { IBusiness, IContact, IContactInfo } from '@trii/types/dist/Contacts';
import { Spaces } from '@trii/types';
import { useState } from 'react';
import type { MouseEvent } from 'react';

import ContactInfoPopup from '../../../ContactInfoPopup';
import { useEditContactApi } from '../../context/EditContactApiContext';
import { useNavigateContext } from '../../context/NavigateContext';
import { useTranslationContext } from '../../context/TranslationContext';
import { Section } from '../../layout';
import { isContact } from '../../types/is-contact.typeguard';

type Props = {
  business: IBusiness;
  onEdit: () => void;
  language?: 'en' | 'es';
  contactNameFormat?: Spaces.nameFormat;
};

const getMemberName = (member: IContactInfo) =>
  String((member as any).name || (member as any).profileName || (member as any).address || '-');

const BusinessMembersSection = ({ business, onEdit, language, contactNameFormat }: Props) => {
  const t = useTranslationContext();
  const api = useEditContactApi();
  const navigate = useNavigateContext();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedMember, setSelectedMember] = useState<IContact | null>(null);
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState('');
  const [loadingMemberId, setLoadingMemberId] = useState<string | null>(null);

  const members = (business.members ?? []) as IContactInfo[];

  const handleOpenInfoPopover =
    (member: IContactInfo) => async (event: MouseEvent<HTMLElement>) => {
      const targetElement = event.currentTarget;

      if (!api || !member?.id) return;

      setLoadingMemberId(member.id);
      try {
        const response = await api.fetchContact({ contactId: member.id });

        if (isContact(response)) {
          setSelectedMember(response);
          setSelectedAvatarUrl(String((member as any).imageUrl || response.imageUrl || ''));
          setAnchorEl(targetElement);
        }
      } finally {
        setLoadingMemberId(null);
      }
    };

  return (
    <Section
      title={t('editContactModal.businessMembers.title')}
      action={
        <IconButton
          aria-label={t('editContactModal.businessMembers.edit')}
          size="small"
          onClick={onEdit}
        >
          <EditIcon sx={{ fontSize: 18 }} />
        </IconButton>
      }
    >
      {members.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {members.map((member) => {
            const name = getMemberName(member);
            const imageUrl = String((member as any).imageUrl || '');
            const isLoading = loadingMemberId === member.id;

            return (
              <Stack
                key={member.id}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ minWidth: 0, py: 0.5 }}
              >
                <Avatar
                  src={imageUrl}
                  alt={name}
                  onClick={handleOpenInfoPopover(member)}
                  sx={{
                    width: 32,
                    height: 32,
                    cursor: api ? 'pointer' : 'default',
                    bgcolor: (theme) => theme.palette.background.default,
                    color: (theme) => theme.palette.text.secondary,
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  {name}
                </Typography>
                {isLoading ? <CircularProgress size={18} /> : null}
              </Stack>
            );
          })}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('editContactModal.businessMembers.empty')}
        </Typography>
      )}

      <ContactInfoPopup
        contactData={selectedMember ?? undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        navigate={navigate}
        avatarImgUrl={selectedAvatarUrl}
        language={language}
        contactNameFormat={contactNameFormat}
      />
    </Section>
  );
};

export default BusinessMembersSection;
